import { useEffect, useState, useMemo } from "react";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "../../../examples/Tables/DataTable";
import data from "../../dashboard/components/Projects/data";
import useApi from "../../../components/Utils/api";
import toast from "utils/toast";
import { DateRangePicker } from "rsuite";
import { afterToday } from "rsuite/cjs/DateRangePicker/disabledDateUtils";
import reportsLineChartData from "../../dashboard/data/reportsLineChartData";
import Papa from 'papaparse';

const getTxnMethod = (entrMode) => {
  if (!entrMode) return "Other";
  if (entrMode.startsWith("05")) return "Contact";
  if (entrMode.startsWith("07")) return "Contactless";
  if (entrMode.startsWith("02")) return "Mag Stripe";
  if (entrMode.startsWith("01")) return "Manual";
  return "Other";
};

function CardTransactions() {
  const { cardTransactionsReportsColumns } = data();
  const { predefinedRanges } = reportsLineChartData;
  const [transactions, setTransactions] = useState([]);
  const [txnMethodFilter, setTxnMethodFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");

  const filteredTransactions = useMemo(() => {
    return transactions.filter(row => {
      const method = getTxnMethod(row.entrMode);
      const isApproved = row.responseCode === '000' || row.responseCode === '00';

      if (txnMethodFilter !== "All" && method !== txnMethodFilter) return false;
      if (statusFilter === "Success" && !isApproved) return false;
      if (statusFilter === "Failed" && isApproved) return false;
      return true;
    });
  }, [transactions, txnMethodFilter, statusFilter]);

  // Function to get today's date at midnight in AEST timezone
  const getTodayDateAtMidnight = () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Set to start of the day in local time
    return new Date(now.getTime() - now.getTimezoneOffset() * 60000); // Convert to local midnight in AEST
  };

  // Initialize lastMonthDateRange with today's date at midnight as both start and end dates
  const [lastMonthDateRange, setLastMonthDateRange] = useState([
    getTodayDateAtMidnight().toISOString().slice(0, 10),
    getTodayDateAtMidnight().toISOString().slice(0, 10)
  ]);

  const api = useApi();

  // Format date to 'yyyy-MM-dd' and adjust to AEST timezone
  const formatDate = (date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      // Handle cases where date is not a valid Date object
      console.error("Invalid date provided:", date);
      return '';
    }
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 10);
  };

  const getTransactions = (dateRange) => {
    if (!dateRange || dateRange.length !== 2) return;

    const fromDate = new Date(dateRange[0]);
    const toDate = new Date(dateRange[1]);

    const data = {
      fromDate: formatDate(fromDate),
      toDate: formatDate(toDate)
    };
    
    api.post("reports/getcardtransactionSummary", data)
      .then((response) => {
        if (response.data.statusCodeValue === 200) {
          setTransactions(response.data.body);
        } else {
          setTransactions([]);
          const toastId = toast.error("No Record Found", { autoClose: false });
          setTimeout(() => toast.dismiss(toastId), 1000);
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }
        setTransactions([]);
        const toastId = toast.error("No Record Found", { autoClose: false });
        setTimeout(() => toast.dismiss(toastId), 1000);
      });
  };

  const generateCSV = () => {
    if (transactions.length === 0) {
      return '';
    }

    const filteredColumns = cardTransactionsReportsColumns.filter(column => 
      column.Header !== "Action" && column.Header !== "Settlement Allowed"
    );
    const headers = filteredColumns.map(column => column.Header);

    const transactionsWithFilteredColumns = transactions.map(transaction => {
      return filteredColumns.map(column => {
        const accessor = column.accessor;
        const value = typeof accessor === 'function' ? accessor(transaction) : transaction[accessor];
        return value;
      });
    });

    const csvData = [headers, ...transactionsWithFilteredColumns];

    const csv = Papa.unparse(csvData, {
      quotes: true,
    });

    return csv;
  };

  const handleDownloadCSV = () => {
    if (transactions.length === 0) {
      const toastId = toast.error("No records found to download", { autoClose: false }); // Prevents auto-closing
      setTimeout(() => toast.dismiss(toastId), 1000); // Dismisses the toast after 1 second
      return;
    }

    const csvData = generateCSV();

    const currentDate = new Date().toISOString().slice(0, 10);
    const currentTime = new Date().toLocaleTimeString().replace(/:/g, '-');
    const dateTimeString = `${currentDate}_${currentTime}`;

    const fileName = `MATAM-Transactions_${dateTimeString}.csv`;

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  useEffect(() => {
    getTransactions(lastMonthDateRange);
  }, [lastMonthDateRange]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={12}>
            <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2} flexWrap="wrap">
              <MDBox display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <FormControl
                  size="small"
                  sx={{
                    minWidth: 180,
                    "& .MuiOutlinedInput-root": { height: "36.5px" },
                    "& .MuiInputLabel-root": { top: "-4px" },
                    "& .MuiInputLabel-shrink": { top: 0 },
                  }}
                >
                  <InputLabel>Transaction Method</InputLabel>
                  <Select
                    value={txnMethodFilter}
                    label="Transaction Method"
                    onChange={e => setTxnMethodFilter(e.target.value)}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Contact">Contact</MenuItem>
                    <MenuItem value="Contactless">Contactless</MenuItem>
                    <MenuItem value="Mag Stripe">Mag Stripe</MenuItem>
                    <MenuItem value="Manual">Manual</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{
                    minWidth: 160,
                    "& .MuiOutlinedInput-root": { height: "36.5px" },
                    "& .MuiInputLabel-root": { top: "-4px" },
                    "& .MuiInputLabel-shrink": { top: 0 },
                  }}
                >
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={e => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Success">Approved</MenuItem>
                    <MenuItem value="Failed">Declined</MenuItem>
                  </Select>
                </FormControl>

                <DateRangePicker
                  shouldDisableDate={afterToday()}
                  ranges={predefinedRanges}
                  onChange={(dateRange) => {
                    if (dateRange.length === 2) {
                      setLastMonthDateRange(dateRange);
                    }
                  }}
                  format="dd MMMM yyyy"
                  value={
                    lastMonthDateRange.length === 2
                      ? [new Date(lastMonthDateRange[0]), new Date(lastMonthDateRange[1])]
                      : []
                  }
                />
              </MDBox>

              <MDButton
                variant="gradient"
                onClick={handleDownloadCSV}
                color="info"
              >
                Export
              </MDButton>
            </MDBox>

            <DataTable
              table={{ columns: cardTransactionsReportsColumns, rows: filteredTransactions }}
              showTotalEntries={true}
              isSorted={true}
              noEndBorder
              entriesPerPage={true}
              canSearch={true}
            />
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default CardTransactions;
